import "./style.scss";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { patchTaskRow } from "@/src/store/slices/TasksPage/tasksTableSlice";
import { IApplyInfoData, IRowData } from "@/src/types/applicationOnline";
import { ITasksTableRow } from "@/src/types/tasksTable";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { documentsRequests } from "@/src/api/documentsRequests";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { buildTaskCreatePayload, resolveTaskAssigneeIds } from "@/src/utils/tasks/buildTaskCreatePayload";
import {
  buildTaskUpdatePayloadFromTaskRow,
  mapTaskUpdatePayloadToTableRowPatch,
} from "@/src/utils/tasks/buildTaskUpdatePayload";
import BtnDefault from "../../BtnDefault/BtnDefault";
import VerticalLine from "../../VerticalLine/VerticalLine";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import CloseSvg from "@/src/svg/applicationsPage/APPopUp/CloseSvg";
import CancelSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/CancelSvg";
import SendSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/SendSvg";
import DateSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/DateSvg";
import { DocsSvg } from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/DocsSvg";
import UploadSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/UploadSvg";
import SelectorArrowSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/SelectorArrowSvg";
import EditSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/EditSvg";
import CheckBoxSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/CheckBoxSvg";
import CheckBoxEmptySvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/CheckBoxEmptySvg";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import CustomFullNamePopUp from "@/src/components/CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import FormSummaryModal, { FileAttachmentField, FilesByFieldMap } from "@/src/components/ApplyOnline/FormSummaryModal/FormSummaryModal";
import {
  admissionSemesterOptions,
  categoryOptions,
  chooseAssigneeOptions,
  durationOptions,
  formOfEducationOptions,
  intakeOptions,
  mediumOfInstructionOptions,
  taskTypeOptions,
} from "@/src/constants/documentPreparationPopUpConsts";

export type DocumentPreparationSubmissionMode = "create" | "update";

interface CustomDocumentPreparationPopUpProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tableContext?: "applications" | "tasks";
  submissionMode?: DocumentPreparationSubmissionMode;
  taskIdMap?: Record<number, string>;
  onSendSuccess?: () => void;
  onRefreshCurrentStudentUserInfo?: () => Promise<IRowData["userInfo"] | undefined> | void;
}

const resolveStudentId = (applyInfo: IApplyInfoData | ITasksTableRow): number | undefined => {
  if ("task_id" in applyInfo) return applyInfo.student_id;
  return applyInfo.applicant_id;
};

const documentsFileFields: FileAttachmentField[] = [
  { name: "files_passport", title: "Passport" },
  { name: "files_certificate", title: "Certificate" },
  { name: "files_education", title: "Education" },
  { name: "files_another", title: "Another" },
];

const CustomDocumentPreparationPopUp: FC<CustomDocumentPreparationPopUpProps> = ({
  isOpen,
  setIsOpen,
  tableContext = "applications",
  submissionMode = "create",
  taskIdMap = {},
  onSendSuccess,
  onRefreshCurrentStudentUserInfo,
}) => {
  const dispatch = useDispatch();
  const getDateWithOffset = (offsetDays: number) => {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);
    return date.toISOString().split("T")[0];
  };

  const applicationsTable = useSelector((state: RootState) => state.applicationsTable);
  const tasksTable = useSelector((state: RootState) => state.tasksTable);
  const isTasksContext = tableContext === "tasks";
  const selectedRows = isTasksContext ? tasksTable.selectedRows : applicationsTable.selectedRows;
  const [currentApplyInfo, setCurrentApplyInfo] = useState<IApplyInfoData | ITasksTableRow | undefined>();
  const [currentUserInfo, setCurrentUserInfo] = useState<IRowData["userInfo"] | undefined>();
  const [priority, setPriority] = useState<"normal" | "urgent">("normal");
  const [chooseAssignee, setChooseAssignee] = useState<string>("");
  const [taskType, setTaskType] = useState<string>("");
  const [intake, setIntake] = useState<string>("");
  const [admissionSemester, setAdmissionSemester] = useState<string>("");
  const [formOfEducation, setFormOfEducation] = useState<string>("");
  const [course, setCourse] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [mediumOfInstruction, setMediumOfInstruction] = useState<string>("");
  const [duration, setDuration] = useState<string>("");
  const [transferFee, setTransferFee] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [deadlineDate, setDeadlineDate] = useState<string>(getDateWithOffset(1));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSelector, setOpenSelector] = useState<string | null>(null);
  const [isFullNamePopUpOpen, setIsFullNamePopUpOpen] = useState(false);
  const [isUniversityUploadModalOpen, setIsUniversityUploadModalOpen] = useState(false);
  const [pendingUniversityFilesMap, setPendingUniversityFilesMap] = useState<FilesByFieldMap>({});
  const formatDateDisplay = useFormatDateDisplay();
  const { notify } = usePushNotifications();

  useEffect(() => {
    if (!isOpen) return;
    if (selectedRows.length !== 1) return;

    const selectedApplyInfoId = selectedRows[0];
    const applyInfo = isTasksContext
      ? tasksTable.data.find((row) => row.task_id === selectedApplyInfoId)
      : applicationsTable.data.find((row) => row.id === selectedApplyInfoId);
    if (!applyInfo) return;

    setCurrentApplyInfo(applyInfo);

    if (isTasksContext) {
      const taskRow = applyInfo as ITasksTableRow;
      console.groupCollapsed("[DocumentPreparationPopUp] Opened with source task (rework / reject)");
      console.log("Source task (ITasksTableRow):", taskRow);
      console.log("attached_file (will be inherited into new task on Send):", taskRow.attached_file);
      console.groupEnd();

      const whoose = (taskRow.whoose ?? "").trim();
      const matchedAssignee =
        chooseAssigneeOptions.find((option) => option.value === whoose || option.label === whoose)?.value ?? whoose;
      setChooseAssignee(matchedAssignee);
      setTaskType(taskRow.type ?? "");
      setIntake(taskRow.intake ?? "");
      setAdmissionSemester(taskRow.admission_semester ?? "");
      setFormOfEducation(taskRow.form_of_education ?? "");
      setCategory(taskRow.category ?? "");
      const mediumOfInstructionSource = (taskRow.medium_of_instruction ?? "").trim();
      const matchedMediumOfInstruction =
        mediumOfInstructionOptions.find(
          (option) => option.value === mediumOfInstructionSource || option.label === mediumOfInstructionSource,
        )?.value ?? mediumOfInstructionSource;
      setMediumOfInstruction(matchedMediumOfInstruction);
      setDuration(taskRow.duration ?? "");
      setTransferFee(taskRow.transfer_fee ?? "");
      const taskPriority = (taskRow.priority ?? "").toLowerCase();
      if (taskPriority === "urgent" || taskPriority === "normal") setPriority(taskPriority);
      setNote(taskRow.note ?? "");
      if (taskRow.deadline_date) setDeadlineDate(taskRow.deadline_date);
    }

    const loadUserInfo = async () => {
      const applicantId = resolveStudentId(applyInfo);
      if (applicantId == null) return;

      try {
        const userInfo = await fetchApplicantUserInfo(applicantId);
        if (userInfo) {
          setCurrentUserInfo(userInfo);
        if (isTasksContext) {
          const taskRow = applyInfo as ITasksTableRow;
          setCourse(taskRow.selected_course?.trim() || taskRow.course?.trim() || userInfo.course || "");
        } else {
            setCourse(userInfo.course ?? "");
          }
          return;
        }
      } catch (error) {
        console.log("DocumentPreparationPopUp loadUserInfo error:", error);
      }

        if (isTasksContext) {
          const taskRow = applyInfo as ITasksTableRow;
          setCourse(taskRow.selected_course?.trim() || taskRow.course?.trim() || "");
        } else {
        setCourse("");
      }
    };

    loadUserInfo();
  }, [isOpen, selectedRows, isTasksContext, applicationsTable.data, tasksTable.data]);

  useEffect(() => {
    if (isOpen) return;
    setCurrentApplyInfo(undefined);
    setCurrentUserInfo(undefined);
    setPriority("normal");
    setChooseAssignee("");
    setTaskType("");
    setIntake("");
    setAdmissionSemester("");
    setFormOfEducation("");
    setCourse("");
    setCategory("");
    setMediumOfInstruction("");
    setDuration("");
    setTransferFee("");
    setNote("");
    setDeadlineDate(getDateWithOffset(1));
    setIsSubmitting(false);
    setOpenSelector(null);
    setIsFullNamePopUpOpen(false);
    setIsUniversityUploadModalOpen(false);
    setPendingUniversityFilesMap({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setDeadlineDate(priority === "urgent" ? getDateWithOffset(0) : getDateWithOffset(1));
  }, [priority, isOpen]);

  const refreshStudentUserInfoForPopUp = useCallback(async (): Promise<IRowData["userInfo"] | undefined> => {
    const applicantId = currentApplyInfo ? resolveStudentId(currentApplyInfo) : undefined;
    if (applicantId == null) {
      return (await onRefreshCurrentStudentUserInfo?.()) ?? undefined;
    }

    try {
      const userInfo = await fetchApplicantUserInfo(applicantId);
      if (userInfo) setCurrentUserInfo(userInfo);
      await onRefreshCurrentStudentUserInfo?.();
      return userInfo;
    } catch (error) {
      console.log("refreshStudentUserInfoForPopUp error:", error);
      return undefined;
    }
  }, [currentApplyInfo, onRefreshCurrentStudentUserInfo]);

  const buildFormState = () => ({
    whoose: chooseAssignee.trim(),
    type: getLabelByValue(taskTypeOptions, taskType),
    intake: getLabelByValue(intakeOptions, intake),
    admission_semester: getLabelByValue(admissionSemesterOptions, admissionSemester),
    form_of_education: getLabelByValue(formOfEducationOptions, formOfEducation),
    selected_course: course.trim(),
    category: getLabelByValue(categoryOptions, category),
    medium_of_instruction: getLabelByValue(mediumOfInstructionOptions, mediumOfInstruction),
    duration: getLabelByValue(durationOptions, duration),
    transfer_fee: transferFee.trim(),
    priority,
    note: note.trim(),
    deadline_date: deadlineDate,
  });

  const handleSend = async () => {
    if (!currentApplyInfo || !currentUserInfo || isSubmitting) return;

    const applicantId = resolveStudentId(currentApplyInfo);
    if (applicantId == null) {
      notify({
        title: submissionMode === "update" ? "Save failed" : "Send failed",
        message: "Applicant ID is missing.",
        type: "error",
      });
      return;
    }

    if (!chooseAssignee.trim() || !taskType.trim()) {
      notify({
        title: "Validation error",
        message: "Please fill in Choose Assignee and Task Type.",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedCurrentCourse = (currentUserInfo.course ?? "").trim();
      const normalizedSelectedCourse = course.trim();
      const inheritedAttachedFile =
        isTasksContext && currentApplyInfo && "attached_file" in currentApplyInfo
          ? (currentApplyInfo as ITasksTableRow).attached_file ?? null
          : null;
      const { assignee_id, executor_id } = resolveTaskAssigneeIds(currentApplyInfo);
      const formState = buildFormState();
      const isUpdateMode = isTasksContext && submissionMode === "update";

      if (isUpdateMode) {
        const taskRow = currentApplyInfo as ITasksTableRow;
        const updatePayload = buildTaskUpdatePayloadFromTaskRow(taskRow, formState, {
          attachedFile: inheritedAttachedFile,
          applicantId,
          assigneeId: assignee_id,
          executorId: executor_id,
          accounting: currentUserInfo.accounting,
        });

        if (Object.keys(updatePayload).length === 0 && normalizedSelectedCourse === normalizedCurrentCourse) {
          notify({
            title: "No changes",
            message: "No task fields were changed.",
            type: "info",
          });
          setIsOpen(false);
          return;
        }

        if (Object.keys(updatePayload).length > 0) {
          const taskId = taskIdMap[taskRow.task_id] ?? String(taskRow.task_id);
          await tasksPageRequests.update(taskId, updatePayload);
          dispatch(
            patchTaskRow({
              taskId: taskRow.task_id,
              patch: mapTaskUpdatePayloadToTableRowPatch(updatePayload),
            }),
          );
        }

        if (normalizedSelectedCourse !== normalizedCurrentCourse) {
          await applicantsRequests.update(applicantId, { course: normalizedSelectedCourse });
        }

        await onRefreshCurrentStudentUserInfo?.();
        onSendSuccess?.();
        notify({
          title: "Task updated",
          message:
            normalizedSelectedCourse !== normalizedCurrentCourse
              ? "Task updated and student course updated."
              : "Task updated successfully.",
          type: "success",
        });
        setIsOpen(false);
        return;
      }

      const payload = buildTaskCreatePayload({
        applicantId,
        form: formState,
        attachedFile: inheritedAttachedFile,
        assigneeId: assignee_id,
        executorId: executor_id,
        accounting: currentUserInfo.accounting,
      });
      console.group("[POST /tasks/create] Request payload");
      console.log(payload);
      console.log("JSON:", JSON.stringify(payload, null, 2));
      console.groupEnd();
      await tasksPageRequests.create(payload);

      if (normalizedSelectedCourse !== normalizedCurrentCourse) {
        await applicantsRequests.update(applicantId, { course: normalizedSelectedCourse });
      }

      await onRefreshCurrentStudentUserInfo?.();
      onSendSuccess?.();
      notify({
        title: "Task created",
        message:
          normalizedSelectedCourse !== normalizedCurrentCourse
            ? "Task created and student course updated."
            : "Task created successfully.",
        type: "success",
      });
      setIsOpen(false);
    } catch (error) {
      console.error("Send to Document Preparation error:", error);
      notify({
        title: submissionMode === "update" ? "Save failed" : "Send failed",
        message: isTasksContext && submissionMode === "update"
          ? "Failed to update task. Please try again."
          : "Failed to create task. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setIsOpen(false);
  };

  const handleUploadUniversityDocuments = async (filesMap: FilesByFieldMap) => {
    const uploadStudentId = currentApplyInfo ? resolveStudentId(currentApplyInfo) : undefined;
    if (uploadStudentId == null) {
      notify({
        title: "Upload failed",
        message: "Student ID is missing.",
        type: "error",
      });
      return;
    }

    const filesToUpload = [
      ...(filesMap.files_passport ?? []),
      ...(filesMap.files_certificate ?? []),
      ...(filesMap.files_education ?? []),
      ...(filesMap.files_another ?? []),
    ];

    if (!filesToUpload.length) {
      notify({
        title: "Nothing to upload",
        message: "Please attach at least one file.",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let uploadedCount = 0;
      for (const file of filesToUpload) {
        const uploadResult = await documentsRequests.uploadUniversityDocument(uploadStudentId, file);
        if (!uploadResult) {
          continue;
        }
        uploadedCount += 1;
      }

      if (uploadedCount === 0) {
        notify({
          title: "Upload failed",
          message: "Could not upload university documents.",
          type: "error",
        });
        return;
      }

      await onRefreshCurrentStudentUserInfo?.();
      setPendingUniversityFilesMap({});
      notify({
        title: "Documents uploaded",
        message:
          uploadedCount === filesToUpload.length
            ? "University documents were added successfully."
            : `Uploaded ${uploadedCount} of ${filesToUpload.length} files.`,
        type: "success",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLabelByValue = (options: Array<{ value: string; label: string }>, value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  const getValueByLabel = (options: Array<{ value: string; label: string }>, label: string) =>
    options.find((option) => option.label === label)?.value ?? "";

  return (
    <div className={`custom-document-preparation-popUp ${isOpen ? "show" : ""}`}>
      <div className="custom-document-preparation-popUp-header">
        <div className="custom-document-preparation-popUp-header-title">
          <p>{isTasksContext && submissionMode === "update" ? "Edit Task" : "Send to Document Preparation"}</p>
        </div>
        <div className="custom-document-preparation-popUp-header-close" onClick={handleClose}>
          <CloseSvg />
        </div>
      </div>
      <div className="custom-document-preparation-popUp-sub_header">
        <div className="custom-document-preparation-popUp-sub_header-title">
          <p>Applicant Info</p>
        </div>
        <div className="custom-document-preparation-popUp-sub_header-info">
          <div className="custom-document-preparation-popUp-sub_header-info-item fullname">
            <div className="custom-document-preparation-popUp-sub_header-info-item-label">
              <p>Full name (EN)</p>
            </div>
            <VerticalLine />
            <div className="custom-document-preparation-popUp-sub_header-info-item-value">
              <p>{currentUserInfo?.fullname ?? "full name"}</p>
            </div>
          </div>
          <div className="custom-document-preparation-popUp-sub_header-info-item fullname_nat">
            <div className="custom-document-preparation-popUp-sub_header-info-item-label">
              <p>Full name (Nat.)</p>
            </div>
            <VerticalLine />
            <div className="custom-document-preparation-popUp-sub_header-info-item-value">
              <p>{currentUserInfo?.fullnameNat ?? "full name (Nat.)"}</p>
            </div>
          </div>
          <div className="custom-document-preparation-popUp-sub_header-info-item date_of_birth">
            <div className="custom-document-preparation-popUp-sub_header-info-item-label">
              <p>Date of Birth</p>
            </div>
            <VerticalLine />
            <div className="custom-document-preparation-popUp-sub_header-info-item-value">
              <p>{currentUserInfo?.dateOfBirth ? formatDateDisplay(currentUserInfo.dateOfBirth, { dateOnly: true }) : "date of birth"}</p>
            </div>
          </div>
          <div className="custom-document-preparation-popUp-sub_header-info-item nationality">
            <div className="custom-document-preparation-popUp-sub_header-info-item-label">
              <p>Nationality</p>
            </div>
            <VerticalLine />
            <div className="custom-document-preparation-popUp-sub_header-info-item-value">
              <p>{currentUserInfo?.nationality ?? "nationality"}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="custom-document-preparation-popUp-body">
        <div className="custom-document-preparation-popUp-body-items">
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Choose Assignee</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(chooseAssigneeOptions, chooseAssignee)}
                onClick={() => setOpenSelector((prev) => (prev === "chooseAssignee" ? null : "chooseAssignee"))}
              />
              <SelectorArrowSvg />
              {openSelector === "chooseAssignee" && (
                <CustomSingleSelect
                  title="Choose assignee"
                  data={chooseAssigneeOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(chooseAssigneeOptions, chooseAssignee)}
                  onChange={(label) => setChooseAssignee(getValueByLabel(chooseAssigneeOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Task Type</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(taskTypeOptions, taskType)}
                onClick={() => setOpenSelector((prev) => (prev === "taskType" ? null : "taskType"))}
              />
              <SelectorArrowSvg />
              {openSelector === "taskType" && (
                <CustomSingleSelect
                  title="Task type"
                  data={taskTypeOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(taskTypeOptions, taskType)}
                  onChange={(label) => setTaskType(getValueByLabel(taskTypeOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Intake</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(intakeOptions, intake)}
                onClick={() => setOpenSelector((prev) => (prev === "intake" ? null : "intake"))}
              />
              <SelectorArrowSvg />
              {openSelector === "intake" && (
                <CustomSingleSelect
                  title="Intake"
                  data={intakeOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(intakeOptions, intake)}
                  onChange={(label) => setIntake(getValueByLabel(intakeOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Admission Semester</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(admissionSemesterOptions, admissionSemester)}
                onClick={() => setOpenSelector((prev) => (prev === "admissionSemester" ? null : "admissionSemester"))}
              />
              <SelectorArrowSvg />
              {openSelector === "admissionSemester" && (
                <CustomSingleSelect
                  title="Admission semester"
                  data={admissionSemesterOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(admissionSemesterOptions, admissionSemester)}
                  onChange={(label) => setAdmissionSemester(getValueByLabel(admissionSemesterOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Form of Education</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(formOfEducationOptions, formOfEducation)}
                onClick={() => setOpenSelector((prev) => (prev === "formOfEducation" ? null : "formOfEducation"))}
              />
              <SelectorArrowSvg />
              {openSelector === "formOfEducation" && (
                <CustomSingleSelect
                  title="Form of education"
                  data={formOfEducationOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(formOfEducationOptions, formOfEducation)}
                  onChange={(label) => setFormOfEducation(getValueByLabel(formOfEducationOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Course</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-input">
              <input type="text" placeholder="Enter the course..." value={course} onChange={(e) => setCourse(e.target.value)} />
              <EditSvg />
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Category</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(categoryOptions, category)}
                onClick={() => setOpenSelector((prev) => (prev === "category" ? null : "category"))}
              />
              <SelectorArrowSvg />
              {openSelector === "category" && (
                <CustomSingleSelect
                  title="Category"
                  data={categoryOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(categoryOptions, category)}
                  onChange={(label) => setCategory(getValueByLabel(categoryOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Medium of Instruction</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(mediumOfInstructionOptions, mediumOfInstruction)}
                onClick={() => setOpenSelector((prev) => (prev === "mediumOfInstruction" ? null : "mediumOfInstruction"))}
              />
              <SelectorArrowSvg />
              {openSelector === "mediumOfInstruction" && (
                <CustomSingleSelect
                  title="Medium of instruction"
                  data={mediumOfInstructionOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(mediumOfInstructionOptions, mediumOfInstruction)}
                  onChange={(label) => setMediumOfInstruction(getValueByLabel(mediumOfInstructionOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Duration</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-select">
              <input
                readOnly
                value={getLabelByValue(durationOptions, duration)}
                onClick={() => setOpenSelector((prev) => (prev === "duration" ? null : "duration"))}
              />
              <SelectorArrowSvg />
              {openSelector === "duration" && (
                <CustomSingleSelect
                  title="Duration"
                  data={durationOptions.map((option) => option.label)}
                  isSearch={true}
                  allowAdd={false}
                  columnValue={getLabelByValue(durationOptions, duration)}
                  onChange={(label) => setDuration(getValueByLabel(durationOptions, label))}
                  onClose={() => setOpenSelector(null)}
                />
              )}
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Transfer Fee</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-input">
              <input type="text" placeholder="0,00" value={transferFee} onChange={(e) => setTransferFee(e.target.value)} />
              <EditSvg />
            </div>
          </div>
          <div className="custom-document-preparation-popUp-body-items-item">
            <div className="custom-document-preparation-popUp-body-items-item-title">
              <p>Priority</p>
            </div>
            <div className="custom-document-preparation-popUp-body-items-item-checkbox">
              <div
                className={`custom-document-preparation-popUp-body-items-item-checkbox-option ${priority === "normal" ? "active" : ""}`}
                onClick={() => setPriority("normal")}
              >
                {priority === "normal" ? <CheckBoxSvg /> : <CheckBoxEmptySvg />}
                <p>Normal</p>
              </div>
              <div
                className={`custom-document-preparation-popUp-body-items-item-checkbox-option ${priority === "urgent" ? "active" : ""}`}
                onClick={() => setPriority("urgent")}
              >
                {priority === "urgent" ? <CheckBoxSvg /> : <CheckBoxEmptySvg />}
                <p>Urgent</p>
              </div>
            </div>
          </div>
        </div>
        <div className="custom-document-preparation-popUp-body-note">
          <textarea placeholder="Note..." value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
      <div className="custom-document-preparation-popUp-footer">
        <div className="custom-document-preparation-popUp-footer-container">
          <div className="custom-document-preparation-popUp-footer-title">
            <p>Deadline</p>
          </div>
          <div className="custom-document-preparation-popUp-footer-content">
            <div className="custom-document-preparation-popUp-footer-date">
              <input type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} />
              <DateSvg />
            </div>
            <VerticalLine />
            <div className="custom-document-preparation-popUp-footer-files">
              <BtnDefault onlyIcon={true} onClick={() => setIsFullNamePopUpOpen(true)}>
                <DocsSvg />
              </BtnDefault>
              <BtnDefault onClick={() => setIsUniversityUploadModalOpen(true)}>
                <p>Upload Files</p>
                <UploadSvg />
              </BtnDefault>
            </div>
          </div>
          <div className="custom-document-preparation-popUp-footer-buttons">
            <BtnDefault onClick={handleClose}>
              <p>Cancel</p>
              <CancelSvg />
            </BtnDefault>
            <VerticalLine />
            <BtnDefault reverse={true} onClick={handleSend}>
              <p>{isTasksContext && submissionMode === "update" ? "Save" : "Send"}</p>
              <SendSvg />
            </BtnDefault>
          </div>
        </div>
      </div>
      {isSubmitting && (
        <div className="custom-document-preparation-popUp-loader">
          <div className="custom-document-preparation-popUp-loader-spinner" />
          <p>{isTasksContext && submissionMode === "update" ? "Saving task..." : "Sending task..."}</p>
        </div>
      )}
      {isFullNamePopUpOpen && (
        <CustomFullNamePopUp
          isApPopUpFullName={isFullNamePopUpOpen}
          setIsApPopUpFullName={setIsFullNamePopUpOpen}
          currentDataPopUp={currentUserInfo}
          currentStudentId={currentApplyInfo ? resolveStudentId(currentApplyInfo) : undefined}
          currentApplyInfoId={selectedRows[0]}
          tableContext={tableContext}
          className="document-preparation-overlay"
          onRefreshCurrentStudentUserInfo={refreshStudentUserInfoForPopUp}
        />
      )}
      <FormSummaryModal
        isActive={isUniversityUploadModalOpen}
        onClose={() => setIsUniversityUploadModalOpen(false)}
        fields={documentsFileFields}
        selectedFilesMap={pendingUniversityFilesMap}
        title="Add university documents"
        subtitle="Attach files to upload into University documents"
        submitLabel="Upload files →"
        onAttach={async (filesMap) => {
          setPendingUniversityFilesMap(filesMap);
          await handleUploadUniversityDocuments(filesMap);
          setIsUniversityUploadModalOpen(false);
        }}
      />
    </div>
  );
};

export default CustomDocumentPreparationPopUp;
